import { analyzeAndGroupCables, getCableDisplayName } from './cableLogic';
import {
    PDF_CONSTANTS,
    estimateSheathNodeHeight as _estimateSheathNodeHeight,
    estimateRenderableHeight as _estimateRenderableHeight
} from './pdfHeightEstimation';

const getFirstIndex = (cables = []) => {
  if (!cables.length) return Infinity;
  return Math.min(...cables.map(c => c.originalIndex ?? 0));
};

// ==================== FUNZIONI STIMA ALTEZZA ====================
// Delegano al modulo centralizzato pdfHeightEstimation.js

const estimateSheathNodeHeight = (groupList, settings) => _estimateSheathNodeHeight(groupList, settings);
const estimateRenderableHeight = (renderable, settings) => _estimateRenderableHeight(renderable, settings);

const getSingleSheathIdForGroup = (group, allowSheath) => {
  if (!allowSheath) return null;

  const groupCables = group.cables || [];
  const sheathIds = Array.from(new Set(
    groupCables
      .filter(cable => Boolean(cable.guaina))
      .map(cable => cable.guaina)
  ));

  return sheathIds.length === 1 ? sheathIds[0] : null;
};

// ==================== FUNZIONI COSTRUZIONE NODI ====================

const buildSingleNode = (cable, level) => ({
  id: `single-${cable.originalIndex}`,
  level,
  type: 'single',
  cables: [cable],
  firstIndex: cable.originalIndex ?? 0,
  subGroups: []
});

const buildMultipleNode = (group, level, settings) => {
  const firstCable = group.cables[0];
  const label = getCableDisplayName(firstCable, settings?.lingua, settings);
  const baseId = group.key || `multiple-${firstCable.originalIndex}`;

  return {
    id: baseId,
    level,
    type: 'multiple',
    label,
    cables: group.cables,
    firstIndex: getFirstIndex(group.cables),
    subGroups: group.cables.map(cable => buildSingleNode(cable, level + 1))
  };
};

/**
 * Costruisce un singolo nodo sheath dai gruppi logici
 */
const buildSheathNode = (sheathId, groupList, level, settings, subgroupIndex = null, totalSubgroups = null) => {
  const allCables = groupList.flatMap(group => group.cables);
  const subGroups = groupList.map(group =>
    group.isSingle
      ? buildSingleNode(group.cables[0], level + 1)
      : buildMultipleNode(group, level + 1, settings)
  );

  const suffix = subgroupIndex !== null ? `-${subgroupIndex}` : '';
  const cableIndexes = allCables
    .map(cable => cable.originalIndex ?? 0)
    .sort((a, b) => a - b)
    .join('_');
  const id = `sheath-${sheathId}-${level}-${cableIndexes}${suffix}`;

  return {
    id, // ID Deterministico e stabile
    level,
    type: 'sheath',
    sheathId,
    label: `Guaina ${sheathId}`,
    cables: allCables,
    firstIndex: getFirstIndex(allCables),
    subGroups,
    subgroupIndex,
    totalSubgroups
  };
};

/**
 * Divide un nodo sheath in sottogruppi se supera l'altezza massima della pagina
 * 
 * La divisione avviene per "renderables" - elementi che corrispondono a SVG separati:
 * - Ogni cavo multipolare è un renderable
 * - Ogni gruppo multiplo è un renderable
 * - TUTTI i cavi singoli insieme formano UN SOLO renderable (l'SVG della guaina)
 */
const divideSheathIntoSubgroups = (sheathId, groupList, level, settings, dynamicGroupsDivision) => {
  // Se la divisione dinamica non è attiva, ritorna un singolo nodo
  if (!dynamicGroupsDivision) {
    return [buildSheathNode(sheathId, groupList, level, settings)];
  }

  const MAX_HEIGHT = PDF_CONSTANTS.MAX_GROUP_HEIGHT_OTHER_PAGES;
  const BASE_SUBGROUP_HEIGHT = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;

  // Stima l'altezza totale del nodo sheath
  const totalHeight = estimateSheathNodeHeight(groupList, settings);

  // Se entra in una pagina, ritorna un singolo nodo senza numerazione
  if (totalHeight <= MAX_HEIGHT) {
    return [buildSheathNode(sheathId, groupList, level, settings)];
  }

  // Converte i gruppi in "renderables" (elementi che corrispondono a SVG)
  const renderables = [];
  const singleCablesGroups = []; // Gruppi di cavi singoli (vanno tenuti insieme)

  groupList.forEach(group => {
    if (group.isSingle) {
      const cable = group.cables[0];
      if (cable.poli && Array.isArray(cable.poli)) {
        // Cavo multipolare → SVG separato
        renderables.push({
          type: 'multipolar',
          cable,
          groups: [group],
          firstIndex: cable.originalIndex ?? 0
        });
      } else {
        // Cavo singolo → va raggruppato con altri singoli
        singleCablesGroups.push(group);
      }
    } else {
      // Gruppo multiplo → SVG separato
      renderables.push({
        type: 'multiple',
        cables: group.cables,
        groups: [group],
        firstIndex: getFirstIndex(group.cables)
      });
    }
  });

  // Aggiungi tutti i cavi singoli come un unico renderable (condividono l'SVG)
  if (singleCablesGroups.length > 0) {
    const allSingleCables = singleCablesGroups.map(g => g.cables[0]);
    renderables.push({
      type: 'sheathSingles',
      cables: allSingleCables,
      groups: singleCablesGroups,
      firstIndex: getFirstIndex(allSingleCables)
    });
  }

  // Ordina per firstIndex
  renderables.sort((a, b) => a.firstIndex - b.firstIndex);

  // Dividi i renderables in sottogruppi basati sull'altezza
  const subNodes = [];
  let currentRenderables = [];
  let currentHeight = BASE_SUBGROUP_HEIGHT;
  let subgroupIndex = 1;

  for (const renderable of renderables) {
    const renderableHeight = estimateRenderableHeight(renderable, settings);

    // Se aggiungere questo renderable supera il limite E abbiamo già dei renderables
    if (currentHeight + renderableHeight > MAX_HEIGHT && currentRenderables.length > 0) {
      // Salva il sottogruppo corrente
      subNodes.push({
        renderables: [...currentRenderables],
        subgroupIndex: subgroupIndex++
      });

      // Inizia un nuovo sottogruppo
      currentRenderables = [];
      currentHeight = BASE_SUBGROUP_HEIGHT;
    }

    currentRenderables.push(renderable);
    currentHeight += renderableHeight;
  }

  // Aggiungi l'ultimo sottogruppo
  if (currentRenderables.length > 0) {
    subNodes.push({
      renderables: currentRenderables,
      subgroupIndex: subgroupIndex
    });
  }

  const totalSubgroups = subNodes.length;

  // Costruisci i nodi sheath con la numerazione x/y
  return subNodes.map(sub => {
    // Ricostruisci i gruppi dai renderables
    const groups = sub.renderables.flatMap(r => r.groups);
    return buildSheathNode(sheathId, groups, level, settings, sub.subgroupIndex, totalSubgroups);
  });
};

/**
 * Divide i gruppi di una guaina in tratte di numerazione contigua.
 *
 * Una guaina i cui cavi saltano delle posizioni (es. #1 e #6) trascinerebbe i cavi
 * lontani sotto al primo della guaina, rompendo l'ordine dell'elenco: è lo stesso
 * motivo per cui cavi standard e multipolari si uniscono solo se adiacenti.
 * I gruppi restano atomici, si spezza solo dove c'è un buco di numerazione.
 */
const splitSheathGroupsIntoRuns = (groupList = []) => {
  const groupsWithSpan = groupList
    .map(group => {
      const indexes = (group.cables || []).map(cable => cable.originalIndex ?? 0);
      return {
        group,
        minIndex: indexes.length ? Math.min(...indexes) : 0,
        maxIndex: indexes.length ? Math.max(...indexes) : 0
      };
    })
    .sort((a, b) => a.minIndex - b.minIndex);

  const runs = [];
  let currentRun = null;
  let currentMaxIndex = null;

  groupsWithSpan.forEach(({ group, minIndex, maxIndex }) => {
    if (currentRun && minIndex <= currentMaxIndex + 1) {
      currentRun.push(group);
      currentMaxIndex = Math.max(currentMaxIndex, maxIndex);
      return;
    }

    currentRun = [group];
    runs.push(currentRun);
    currentMaxIndex = maxIndex;
  });

  return runs;
};

/**
 * Costruisce i sottogruppi per un insieme di cavi
 * Supporta la divisione dinamica per i nodi sheath
 */
const buildSubGroups = (cables = [], level, settings, options = { allowSheath: true, dynamicGroupsDivision: false }) => {
  if (!cables || cables.length === 0) return [];

  const groups = analyzeAndGroupCables(cables, settings);
  const resultNodes = [];
  const sheathBuckets = new Map();

  groups.forEach(group => {
    const sheathId = getSingleSheathIdForGroup(group, options.allowSheath);

    if (sheathId) {
      if (!sheathBuckets.has(sheathId)) {
        sheathBuckets.set(sheathId, []);
      }
      sheathBuckets.get(sheathId).push(group);
      return;
    }

    if (group.isSingle) {
      group.cables.forEach(cable => {
        resultNodes.push(buildSingleNode(cable, level));
      });
    } else {
      resultNodes.push(buildMultipleNode(group, level, settings));
    }
  });

  sheathBuckets.forEach((groupList, sheathId) => {
    const sheathNodes = splitSheathGroupsIntoRuns(groupList).flatMap(runGroups =>
      divideSheathIntoSubgroups(
        sheathId,
        runGroups,
        level,
        settings,
        options.dynamicGroupsDivision
      )
    );

    // Se la guaina finisce in più box (per buchi di numerazione o per altezza pagina)
    // vanno numerati x/y, altrimenti sarebbero indistinguibili.
    if (sheathNodes.length > 1) {
      sheathNodes
        .sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0))
        .forEach((node, index) => {
          node.subgroupIndex = index + 1;
          node.totalSubgroups = sheathNodes.length;
        });
    }

    resultNodes.push(...sheathNodes);
  });

  return resultNodes.sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
};

const buildTopLevelNode = ({
  id,
  level,
  label,
  type = 'group',
  cables = [],
  settings,
  allowSheath = true,
  groupCode,
  groupKey,
  defaultTitle,
  subgroupIndex,
  totalSubgroups,
  dynamicGroupsDivision = false
}) => {
  if (type === 'single') {
    const cable = cables[0];
    return {
      ...buildSingleNode(cable, level),
      label: getCableDisplayName(cable, settings?.lingua, settings)
    };
  }

  const resolvedLabel = defaultTitle || label;

  return {
    id,
    level,
    type,
    label: resolvedLabel,
    defaultTitle: resolvedLabel,
    groupCode,
    groupKey,
    cables,
    firstIndex: getFirstIndex(cables),
    subGroups: buildSubGroups(cables, level + 1, settings, { allowSheath, dynamicGroupsDivision }),
    subgroupIndex: subgroupIndex ?? cables?.[0]?.subgroupIndex ?? null,
    totalSubgroups: totalSubgroups ?? cables?.[0]?.totalSubgroups ?? null
  };
};

// ==================== FUNZIONE PRINCIPALE ====================

export const buildNestedCableStructure = ({
  groups = [],
  settings,
  forcedGroups = [],
  normalCables = [],
  dynamicGroupsDivision = false
}) => {
  if (groups.length > 0) {
    return groups.map(group => {
      if (group.isForcedGroup) {
        const groupName = group.groupData?.groupName || 'Forced Group';
        const subgroupIndex = group.groupData?.subgroupIndex || null;
        const totalSubgroups = group.groupData?.totalSubgroups || null;
        return buildTopLevelNode({
          id: group.key || groupName,
          level: 1,
          type: 'group',
          label: groupName,
          groupCode: groupName,
          groupKey: group.key || groupName,
          defaultTitle: groupName,
          cables: group.groupData?.allCables || [],
          settings,
          subgroupIndex,
          totalSubgroups,
          dynamicGroupsDivision
        });
      }

      const sortedGroup = group.sortedGroupData || { cables: group.cables || [] };
      const firstCable = sortedGroup.cables?.[0];
      const label = firstCable ? getCableDisplayName(firstCable, settings?.lingua, settings) : 'Group';
      const isSheath = sortedGroup.key && sortedGroup.key.startsWith('sheath-');
      const subgroupIndex = sortedGroup.cables?.[0]?.subgroupIndex || null;
      const totalSubgroups = sortedGroup.cables?.[0]?.totalSubgroups || null;
      const baseGroupCode = firstCable?.originalSiglatura || group.key;

      return buildTopLevelNode({
        id: group.key,
        level: 1,
        type: isSheath ? 'sheath' : 'group',
        label,
        cables: sortedGroup.cables || [],
        settings,
        allowSheath: !isSheath,
        groupCode: baseGroupCode,
        subgroupIndex,
        totalSubgroups,
        dynamicGroupsDivision
      });
    }).sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
  }

  const nodes = [];

  forcedGroups.forEach(group => {
    if (group.isForcedGroup) {
      const groupName = group.groupData?.groupName || 'Forced Group';
      nodes.push(buildTopLevelNode({
        id: group.key || groupName,
        level: 1,
        type: 'group',
        label: groupName,
        groupCode: groupName,
        groupKey: group.key || groupName,
        defaultTitle: groupName,
        cables: group.groupData?.allCables || [],
        settings,
        subgroupIndex: group.groupData?.subgroupIndex || null,
        totalSubgroups: group.groupData?.totalSubgroups || null,
        dynamicGroupsDivision
      }));
    }
  });

  if (normalCables && normalCables.length > 0) {
    const groupMap = new Map();
    const ungroupedCables = [];

    normalCables.forEach(cable => {
      const groupCode = typeof cable.gruppo === 'string' ? cable.gruppo.trim() : '';
      if (groupCode) {
        if (!groupMap.has(groupCode)) {
          groupMap.set(groupCode, []);
        }
        groupMap.get(groupCode).push(cable);
      } else {
        ungroupedCables.push(cable);
      }
    });

    groupMap.forEach((groupCables, groupCode) => {
      const firstCable = groupCables?.[0];
      const baseGroupCode = firstCable?.originalSiglatura || groupCode;
      nodes.push(buildTopLevelNode({
        id: groupCode,
        level: 1,
        type: 'group',
        label: baseGroupCode,
        groupCode: baseGroupCode,
        groupKey: groupCode,
        defaultTitle: baseGroupCode,
        cables: groupCables,
        settings,
        dynamicGroupsDivision
      }));
    });

    if (ungroupedCables.length > 0) {
      // Per i cavi non raggruppati, usa buildSubGroups con la divisione dinamica
      const normalNodes = buildSubGroups(ungroupedCables, 1, settings, {
        allowSheath: true,
        dynamicGroupsDivision
      });
      nodes.push(...normalNodes);
    }
  }

  return nodes.sort((a, b) => (a.firstIndex ?? 0) - (b.firstIndex ?? 0));
};
